// pages/Home.jsx
import React from "react";
import { Link } from "react-router-dom";
import "../styles/Home.css";
import OptimizedImage from "../components/OptimizedImage";

import { workExperiences } from "../data/workData";
import { languageIcons, projects } from "../data/projectsData";
import { writingPieces } from "../data/writingData";

import Arthur from "../images/about_me/Arthur.jpeg";
import Image1 from "../images/home_page/image1.jpeg";
import Image2 from "../images/home_page/image2.jpeg";
import Image3 from "../images/home_page/image3.jpeg";
import Image4 from "../images/home_page/image4.jpeg";
import Image5 from "../images/home_page/image5.jpeg";
import Image6 from "../images/home_page/image6.jpeg";
import GitHubSquare from "../images/home_page/GitHubSquare.png";
import LinkedInSquare from "../images/home_page/LinkedInSquare.png";

const images = [
  {
    src: Image1,
    alt: "Arthur - Photo 1",
    description: "NLDC Spring Showcase\nLast Shift with Event Management",
    date: "April 2026",
  },
  {
    src: Image2,
    alt: "Arthur - Photo 2",
    description: "Mass General Cancer 5K Run",
    date: "September 2025",
  },
  {
    src: Image3,
    alt: "Arthur - Photo 3",
    description: "Palisades Tahoe",
    date: "March 2025",
  },
  {
    src: Image4,
    alt: "Arthur - Photo 4",
    description: "Apple Park at 5 AM",
    date: "August 2025",
  },
  {
    src: Image5,
    alt: "Arthur - Photo 5",
    description: "TQL Stadium, Cincinnati\nField Day, Verizon Co-op",
    date: "January 2025",
  },
  {
    src: Image6,
    alt: "Arthur - Photo 6",
    description: "Team Lunch\nAirPlay Ecosystem QE Intern",
    date: "March 2024",
  },
];

const allWork = Object.entries(workExperiences);

const allProjects = [
  {
    key: "quantifyingHappiness",
    data: projects.quantifyingHappiness,
    path: "/projects/quantifyinghappiness",
  },
  {
    key: "audioAnalyzer",
    data: projects.audioAnalyzer,
    path: "/projects/audioanalyzer",
  },
  { key: "qhelper", data: projects.qhelper, path: "/projects/qhelper" },
  { key: "quizzer", data: projects.quizzer, path: "/projects/quizzer" },
  { key: "mileage", data: projects.mileage, path: "/projects/mileage" },
  {
    key: "awayFromHome",
    data: projects.awayFromHome,
    path: "/projects/awayfromhome",
  },
].filter(({ data }) => data);

const allWriting = Object.entries(writingPieces);

const Home = () => {
  return (
    <>
      <title>Arthur Efremenko</title>
      <meta name="description" content="Arthur's personal website." />

      <div className="home-page">
        {/* About Me */}
        <section className="home-intro" id="home-intro" aria-label="About me">
          <div className="home-intro-image-section">
            <OptimizedImage
              src={Arthur}
              alt="Arthur Efremenko"
              className="home-intro-image"
              eager
            />
          </div>

          <div className="home-intro-text">
            <h1 className="home-intro-name">Arthur Efremenko</h1>
            <p className="home-intro-role">SWE @ Apple</p>
            <p className="home-intro-education">
              CS and Physics, Northeastern University
            </p>

            <div className="home-social-links">
              <a
                href="https://github.com/aefremenko24"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="GitHub Profile"
              >
                <OptimizedImage
                  src={GitHubSquare}
                  alt="GitHub"
                  className="social-icon"
                  eager
                />
              </a>
              <a
                href="https://linkedin.com/in/efremenkoa"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="LinkedIn Profile"
              >
                <OptimizedImage
                  src={LinkedInSquare}
                  alt="LinkedIn"
                  className="social-icon"
                  eager
                />
              </a>
            </div>
          </div>
        </section>

        {/* Photo grid */}
        <section className="home-gallery" aria-label="Photos">
          <div className="home-grid">
            {images.map((image, index) => (
              <div
                key={index}
                className={`home-image-wrapper home-image-${index + 1}`}
              >
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  className="home-image"
                  eager={index < 3}
                />
                <div className="home-image-overlay">
                  <span className="home-image-description">
                    {image.description}
                  </span>
                  <span className="home-image-date">{image.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Work */}
        <section className="home-section" aria-label="Work experience">
          <h2 className="home-section-title">Work</h2>

          <div className="home-work-list">
            {allWork.map(([key, experience]) => (
              <div key={key} className="home-work-card">
                {experience.logoRef ? (
                  <OptimizedImage
                    src={experience.logoRef}
                    alt={experience.logoAlt}
                    className="home-work-logo"
                  />
                ) : (
                  <div className="home-work-logo-placeholder">
                    {experience.company.charAt(0)}
                  </div>
                )}

                <div className="home-work-right">
                  <p className="home-work-meta">
                    <span className="home-work-company">
                      {experience.company}
                    </span>
                    {`, ${experience.location}`}
                  </p>
                  <h3 className="home-work-role">{experience.role}</h3>
                  <p className="home-work-dates">
                    {experience.startDate} - {experience.endDate || "Present"}
                  </p>
                  <p className="home-work-description">
                    {experience.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Projects */}
        <section className="home-section" aria-label="Projects">
          <h2 className="home-section-title">Projects</h2>

          <div className="home-project-list">
            {allProjects.map(({ key, data, path }) => (
              <Link key={key} to={path} className="home-project-card">
                <div className="home-project-left">
                  <OptimizedImage
                    src={data.logoRef}
                    alt={data.logoAlt}
                    className="home-project-icon"
                  />
                  <div className="home-project-languages">
                    {data.languages.map((lang) => (
                      <OptimizedImage
                        key={lang}
                        src={languageIcons[lang]}
                        alt="Programming language logo"
                        className="home-language-icon"
                      />
                    ))}
                  </div>
                </div>

                <p className="home-project-description">
                  <span className="home-project-name">{data.name}:</span>{" "}
                  {data.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Writing */}
        <section className="home-section" aria-label="Writing">
          <h2 className="home-section-title">Writing</h2>

          <div className="home-writing-list">
            {allWriting.map(([key, article]) => (
              <Link
                key={key}
                to={`/writing/${article.slug}`}
                className="home-article-card"
              >
                <h3 className="home-article-title">{article.title}</h3>
                {article.date && (
                  <time className="home-article-date">{article.date}</time>
                )}
                <p className="home-article-preview">{article.preview}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
